import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Save,
  Play,
  AlertCircle,
  Info,
  Trash2,
  Plus,
  FileText,
  Coins,
  Percent,
  Hash,
  Download,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import type { DisposalBatch, DisposalItem } from '@/lib/mock-disposals-data';
import { dataService } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';

const getDisposalExportColumns = (): ExportColumn<DisposalItem>[] => [
  { label: 'Asset Code', key: 'assetCode' as keyof DisposalItem },
  { label: 'Asset Description', key: 'assetDescription' as keyof DisposalItem },
  { label: 'Category', key: 'category' as keyof DisposalItem },
  { label: 'Location', key: 'location' as keyof DisposalItem },
  { label: 'Current Cost', key: 'currentCost' as keyof DisposalItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Current Depreciation', key: 'currentDepreciation' as keyof DisposalItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Current NBV', key: 'currentNBV' as keyof DisposalItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Proceeds', key: 'proceedsValue' as keyof DisposalItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Gain/Loss', key: 'gainLoss' as keyof DisposalItem, format: (v: number) => formatCurrencyForExport(v) },
  { label: 'Notes', key: 'notes' as keyof DisposalItem },
];
import type { DisposalReason } from '@/types/settings';
import type { Asset } from '@/types/asset';

interface DisposalEntryScreenProps {
  batch: DisposalBatch;
  onClose: () => void;
  onSave: (batch: DisposalBatch) => void;
}

type DisposeOfMode = 'value' | 'percentage' | 'quantity';
type DepreciationHandling = 'reverse' | 'dispose';

interface DisposalValues {
  actualCost: number;
  historicalCost: number;
  actualDepreciation: number;
  historicalDepreciation: number;
  proceedsValue: number;
}

export function DisposalEntryScreen({
  batch: initialBatch,
  onClose,
  onSave,
}: DisposalEntryScreenProps): React.ReactElement {
  const navigate = useNavigate();
  
  // State for batch header
  const [batchReference, setBatchReference] = useState(initialBatch?.batchReference || '');
  const [dateOfRun, setDateOfRun] = useState(
    initialBatch?.dateOfRun || new Date().toISOString().split('T')[0]
  );
  const [year, setYear] = useState(initialBatch?.year ?? new Date().getFullYear());
  const [period, setPeriod] = useState(initialBatch?.period ?? 1);
  const [disposalReasonId, setDisposalReasonId] = useState(initialBatch?.disposalReasonId ?? '');
  const [notes, setNotes] = useState(initialBatch?.notes || '');
  
  // State for disposal options (single asset only)
  const [disposeOfMode, setDisposeOfMode] = useState<DisposeOfMode>(
    initialBatch?.disposeOfMode || 'value'
  );
  const [disposalQuantity, setDisposalQuantity] = useState(0);
  const [depreciationHandling, setDepreciationHandling] = useState<DepreciationHandling | undefined>(
    initialBatch?.depreciationHandling
  );
  
  // State for disposal values
  const [disposalValues, setDisposalValues] = useState<DisposalValues>({
    actualCost: 0,
    historicalCost: 0,
    actualDepreciation: 0,
    historicalDepreciation: 0,
    proceedsValue: 0,
  });
  
  // State for disposal items
  const [items, setItems] = useState<DisposalItem[]>(initialBatch?.items || []);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  
  // State for disposal reasons
  const [disposalReasons, setDisposalReasons] = useState<DisposalReason[]>([]);
  
  // State for modals
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Computed values
  const isSingleAsset = items.length === 1;
  const isMultipleAssets = items.length > 1;
  const currentAsset = isSingleAsset ? items[0] : null;
  const currentQuantity = currentAsset ? 1 : 0; // TODO: Get from asset data
  const isReadOnly = initialBatch?.status === 'Completed';
  const currentSystemYear = new Date().getFullYear(); // TODO: Fetch from system period settings
  const currentSystemPeriod = new Date().getMonth() + 1; // TODO: Fetch from system period settings
  const isCurrentPeriod = year === currentSystemYear && period === currentSystemPeriod;
  const isBacktrackedDisposal = year < currentSystemYear || (year === currentSystemYear && period < currentSystemPeriod);
  const hasDepreciationInCurrentPeriod = false; // TODO: Check from asset data via API
  // Task 4.3: Disable Value mode when depreciation has been posted in current period
  const shouldDisableValueMode = hasDepreciationInCurrentPeriod && isCurrentPeriod;
  const showDepreciationHandling = hasDepreciationInCurrentPeriod && isCurrentPeriod;
  const allSelected = items.length > 0 && selectedItemIds.size === items.length;

  // Load disposal reasons
  useEffect(() => {
    const loadDisposalReasons = async () => {
      try {
        const reasons = await dataService.getDisposalReasons();
        setDisposalReasons(reasons);
      } catch (error) {
        console.error('Failed to load disposal reasons:', error);
        toast.error('Failed to load disposal reasons');
      }
    };
    loadDisposalReasons();
  }, []);

  // Task 4.2: Set default disposal mode based on whether it's a backtracked disposal
  // Backtracked disposals (prior period) should default to 'percentage' mode
  useEffect(() => {
    if (isBacktrackedDisposal && !initialBatch?.disposeOfMode) {
      setDisposeOfMode('percentage');
    }
    // Task 4.3: If Value mode is disabled and currently selected, switch to percentage
    if (shouldDisableValueMode && disposeOfMode === 'value') {
      setDisposeOfMode('percentage');
    }
  }, [isBacktrackedDisposal, shouldDisableValueMode, disposeOfMode, initialBatch?.disposeOfMode]);

  // Initialize disposal values when items change
  useEffect(() => {
    if (items.length === 0) return;

    if (isSingleAsset) {
      // Single asset - use asset values
      const asset = items[0];
      setDisposalValues({
        actualCost: asset.currentCost,
        historicalCost: asset.historicalCost,
        actualDepreciation: asset.currentDepreciation,
        historicalDepreciation: asset.historicalDepreciation,
        proceedsValue: 0,
      });
    } else {
      // Multiple assets - sum all values
      const totals = items.reduce(
        (acc, item) => ({
          actualCost: acc.actualCost + item.currentCost,
          historicalCost: acc.historicalCost + item.historicalCost,
          actualDepreciation: acc.actualDepreciation + item.currentDepreciation,
          historicalDepreciation: acc.historicalDepreciation + item.historicalDepreciation,
          proceedsValue: 0,
        }),
        { actualCost: 0, historicalCost: 0, actualDepreciation: 0, historicalDepreciation: 0, proceedsValue: 0 }
      );
      setDisposalValues(totals);
    }
  }, [items, isSingleAsset]);

  // Handle dispose of mode change
  useEffect(() => {
    if (disposeOfMode === 'percentage') {
      // Set all values to 100%
      setDisposalValues(prev => ({
        actualCost: 100,
        historicalCost: 100,
        actualDepreciation: 100,
        historicalDepreciation: 100,
        proceedsValue: prev.proceedsValue,
      }));
    } else if (disposeOfMode === 'value') {
      // Reset to calculated values
      if (isSingleAsset) {
        const asset = items[0];
        setDisposalValues(prev => ({
          actualCost: asset.currentCost,
          historicalCost: asset.historicalCost,
          actualDepreciation: asset.currentDepreciation,
          historicalDepreciation: asset.historicalDepreciation,
          proceedsValue: prev.proceedsValue,
        }));
      } else if (isMultipleAssets) {
        const totals = items.reduce(
          (acc, item) => ({
            actualCost: acc.actualCost + item.currentCost,
            historicalCost: acc.historicalCost + item.historicalCost,
            actualDepreciation: acc.actualDepreciation + item.currentDepreciation,
            historicalDepreciation: acc.historicalDepreciation + item.historicalDepreciation,
            proceedsValue: 0,
          }),
          { actualCost: 0, historicalCost: 0, actualDepreciation: 0, historicalDepreciation: 0, proceedsValue: 0 }
        );
        setDisposalValues(prev => ({
          ...totals,
          proceedsValue: prev.proceedsValue,
        }));
      }
    } else if (disposeOfMode === 'quantity' && isSingleAsset) {
      // Calculate proportional values based on quantity
      if (disposalQuantity > 0 && currentQuantity > 0) {
        const asset = items[0];
        const ratio = disposalQuantity / currentQuantity;
        setDisposalValues(prev => ({
          actualCost: asset.currentCost * ratio,
          historicalCost: asset.historicalCost * ratio,
          actualDepreciation: asset.currentDepreciation * ratio,
          historicalDepreciation: asset.historicalDepreciation * ratio,
          proceedsValue: prev.proceedsValue,
        }));
      }
    }
  }, [disposeOfMode, disposalQuantity, currentQuantity, isSingleAsset, isMultipleAssets, items]);

  // Calculate disposal details for each item
  const calculatedItems: DisposalItem[] = items.map((item) => {
    let disposalCost = 0;
    let disposalDepreciation = 0;
    let proceeds = 0;

    if (disposeOfMode === 'percentage') {
      // Calculate based on percentage
      disposalCost = (item.currentCost * disposalValues.actualCost) / 100;
      disposalDepreciation = (item.currentDepreciation * disposalValues.actualDepreciation) / 100;
      proceeds = disposalValues.proceedsValue;
    } else if (disposeOfMode === 'value') {
      if (isSingleAsset) {
        // Single asset - use entered values
        disposalCost = disposalValues.actualCost;
        disposalDepreciation = disposalValues.actualDepreciation;
        proceeds = disposalValues.proceedsValue;
      } else {
        // Multiple assets - distribute proportionally
        const totalCost = items.reduce((sum, i) => sum + i.currentCost, 0);
        const totalDepr = items.reduce((sum, i) => sum + i.currentDepreciation, 0);
        
        if (totalCost > 0) {
          disposalCost = (item.currentCost / totalCost) * disposalValues.actualCost;
        }
        if (totalDepr > 0) {
          disposalDepreciation = (item.currentDepreciation / totalDepr) * disposalValues.actualDepreciation;
        }
        proceeds = (item.currentCost / totalCost) * disposalValues.proceedsValue;
      }
    } else if (disposeOfMode === 'quantity' && isSingleAsset) {
      // Calculate based on quantity
      const ratio = disposalQuantity / currentQuantity;
      disposalCost = item.currentCost * ratio;
      disposalDepreciation = item.currentDepreciation * ratio;
      proceeds = disposalValues.proceedsValue;
    }

    const disposalNBV = disposalCost - disposalDepreciation;
    const gainLoss = proceeds - disposalNBV;

    return {
      ...item,
      disposalCost,
      disposalDepreciation,
      disposalNBV,
      proceedsValue: proceeds,
      gainLoss,
    };
  });

  // Calculate totals
  const totalDisposalCost = calculatedItems.reduce((sum, item) => sum + item.disposalCost, 0);
  const totalDisposalDepreciation = calculatedItems.reduce((sum, item) => sum + item.disposalDepreciation, 0);
  const totalDisposalNBV = calculatedItems.reduce((sum, item) => sum + item.disposalNBV, 0);
  const totalProceedsValue = calculatedItems.reduce((sum, item) => sum + item.proceedsValue, 0);
  const totalGainLoss = calculatedItems.reduce((sum, item) => sum + item.gainLoss, 0);

  // Mark as having unsaved changes when any field changes (skip initial render)
  const [isInitialRender, setIsInitialRender] = useState(true);
  
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }
    setHasUnsavedChanges(true);
  }, [batchReference, dateOfRun, year, period, disposalReasonId, disposeOfMode, disposalQuantity, depreciationHandling, notes, items, disposalValues]);

  const handleSave = async () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before saving.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before saving.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the disposal batch.',
      });
      return;
    }

    const now = new Date().toISOString();
    const selectedReason = disposalReasons.find(r => r.id === disposalReasonId);
    
    const batch: DisposalBatch = {
      id: initialBatch?.id || `batch-${Date.now()}`,
      batchReference,
      dateOfRun,
      periodType: 'current',
      year,
      period,
      disposalReasonId,
      disposalReasonCode: selectedReason?.code,
      disposalReasonDescription: selectedReason?.description,
      disposeOfMode,
      depreciationHandling,
      notes,
      items: calculatedItems,
      status: 'In Progress',
      preparedBy: initialBatch?.preparedBy || 'Current User',
      preparedDate: initialBatch?.preparedDate || now,
      totalDisposalValue: totalDisposalNBV,
      totalProceedsValue,
      totalGainLoss,
      currency: 'GBP',
      createdBy: initialBatch?.createdBy || 'Current User',
      createdDate: initialBatch?.createdDate || now,
      modifiedBy: 'Current User',
      modifiedDate: now,
    };

    try {
      onSave(batch);
      setHasUnsavedChanges(false);
      toast.success('Batch saved successfully');
    } catch (error) {
      console.error('Failed to save disposal batch:', error);
      toast.error('Failed to save batch', {
        description: 'Please try again.',
      });
    }
  };

  const handleProcess = () => {
    // Validate required fields
    if (!batchReference.trim()) {
      toast.error('Reference is required', {
        description: 'Please enter a batch reference before processing.',
      });
      return;
    }

    if (!dateOfRun) {
      toast.error('Date is required', {
        description: 'Please select a date before processing.',
      });
      return;
    }

    if (items.length === 0) {
      toast.error('No assets selected', {
        description: 'Please add at least one asset to the disposal batch.',
      });
      return;
    }

    // Validate that at least one item has a non-zero disposal value
    const hasValidItems = calculatedItems.some(item => item.disposalCost > 0 || item.disposalDepreciation > 0);
    if (!hasValidItems) {
      toast.error('No disposal values', {
        description: 'Please ensure at least one asset has a non-zero disposal value.',
      });
      return;
    }

    // Validate disposal values
    if (disposeOfMode === 'percentage') {
      if (disposalValues.actualCost > 100 || disposalValues.actualDepreciation > 100) {
        toast.error('Invalid percentage', {
          description: 'Percentage values cannot exceed 100%.',
        });
        return;
      }
      if (disposalValues.actualCost === 100 && disposalValues.actualDepreciation !== 100) {
        toast.error('Invalid disposal', {
          description: 'If disposing 100% of asset cost, you must also dispose 100% of depreciation.',
        });
        return;
      }
    } else if (disposeOfMode === 'value') {
      const maxCost = isSingleAsset ? items[0].currentCost : items.reduce((sum, i) => sum + i.currentCost, 0);
      if (disposalValues.actualCost > maxCost) {
        toast.error('Invalid disposal value', {
          description: 'Cannot dispose more than the asset cost.',
        });
        return;
      }
      if (disposalValues.actualCost === maxCost) {
        const maxDepr = isSingleAsset ? items[0].currentDepreciation : items.reduce((sum, i) => sum + i.currentDepreciation, 0);
        if (disposalValues.actualDepreciation !== maxDepr) {
          toast.error('Invalid disposal', {
            description: 'If disposing the full asset cost, you must dispose all accumulated depreciation.',
          });
          return;
        }
      }
    } else if (disposeOfMode === 'quantity') {
      if (disposalQuantity <= 0 || disposalQuantity > currentQuantity) {
        toast.error('Invalid quantity', {
          description: `Disposal quantity must be between 1 and ${currentQuantity}.`,
        });
        return;
      }
    }

    setShowProcessConfirm(true);
  };

  const confirmProcess = async () => {
    try {
      const selectedReason = disposalReasons.find(r => r.id === disposalReasonId);
      const massDisposalRequest = {
        reference: batchReference,
        year: year || new Date().getFullYear(),
        period: period || new Date().getMonth() + 1,
        disposalReasonId,
        disposalReasonCode: selectedReason?.code,
        notes,
        disposals: calculatedItems.map(item => ({
          assetId: item.assetId,
          disposeOfMode,
          disposalCost: item.disposalCost,
          disposalDepreciation: item.disposalDepreciation,
          disposalPercentage: disposeOfMode === 'percentage' ? disposalValues.actualCost : undefined,
          disposalQuantity: disposeOfMode === 'quantity' ? disposalQuantity : undefined,
          proceedsValue: item.proceedsValue,
          depreciationHandling,
        })),
      };
      
      await dataService.submitMassDisposal(massDisposalRequest);
      
      toast.success('Disposal batch processed successfully', {
        description: `Reference: ${batchReference}`,
      });
      setShowProcessConfirm(false);
      onClose();
      navigate('/transactions/disposals');
    } catch (error) {
      console.error('Failed to process disposal batch:', error);
      toast.error('Failed to process disposal batch', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
      setShowProcessConfirm(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  const handleRemoveSelectedItems = () => {
    if (selectedItemIds.size === 0) return;
    setShowRemoveConfirm(true);
  };

  const confirmRemoveSelectedItems = () => {
    setItems(prevItems => prevItems.filter(item => !selectedItemIds.has(item.id)));
    setSelectedItemIds(new Set());
    setShowRemoveConfirm(false);
  };

  const handleAddMoreAssets = () => {
    setShowAssetSelection(true);
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Add new assets to the disposal batch
    const newItems: DisposalItem[] = selectedAssets.map((asset, index) => ({
      id: `disp-item-${Date.now()}-${index}`,
      assetId: asset.id,
      assetCode: asset.code,
      assetDescription: asset.name,
      category: asset.category ?? '',
      location: asset.location ?? '',
      currentCost: asset.acquisitionCost ?? 0,
      currentDepreciation: asset.accumulatedDepreciation ?? 0,
      currentNBV: asset.netBookValue ?? 0,
      historicalCost: asset.acquisitionCost ?? 0,
      historicalDepreciation: asset.accumulatedDepreciation ?? 0,
      historicalNBV: asset.netBookValue ?? 0,
      disposalCost: 0,
      disposalDepreciation: 0,
      disposalNBV: 0,
      proceedsValue: 0,
      gainLoss: 0,
      notes: '',
    }));

    setItems([...items, ...newItems]);
    setShowAssetSelection(false);
    setHasUnsavedChanges(true);
    toast.success(`Added ${selectedAssets.length} asset${selectedAssets.length === 1 ? '' : 's'} to disposal batch`);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItemIds(new Set(items.map(item => item.id)));
    } else {
      setSelectedItemIds(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const selectedReason = disposalReasons.find(r => r.id === disposalReasonId);

  const handleExportCSV = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToCSV({
        data: items,
        columns: getDisposalExportColumns(),
        filename: generateFilename(`disposal-batch-${batchReference || 'draft'}`, 'csv'),
      });

      toast.success('Export successful', {
        description: 'Disposal batch exported to CSV',
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  const handleExportExcel = () => {
    if (items.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      exportToExcel({
        data: items,
        columns: getDisposalExportColumns(),
        filename: generateFilename(`disposal-batch-${batchReference || 'draft'}`, 'xlsx'),
        sheetName: 'Disposal Details',
      });

      toast.success('Export successful', {
        description: 'Disposal batch exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  return (
    <div className="flex h-full flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Compact Header */}
      <div className="border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-sm flex-shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={handleCancel} className="h-9 w-9">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-3">
                <Trash2 className="h-5 w-5 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">
                    {batchReference ? `Disposal - ${batchReference}` : 'New Disposal'}
                  </h1>
                  {isReadOnly && initialBatch?.postedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Posted: {new Date(initialBatch.postedDate).toLocaleString()}
                    </p>
                  ) : initialBatch?.modifiedDate ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Last modified: {new Date(initialBatch.modifiedDate).toLocaleString()}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isReadOnly && (
                <>
                  <Button variant="outline" onClick={handleSave} className="h-9">
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </Button>
                  <Button onClick={handleProcess} className="h-9">
                    <Play className="mr-2 h-4 w-4" />
                    Process
                  </Button>
                </>
              )}
              {isReadOnly && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Completed
                </Badge>
              )}
              {items.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-9 w-9">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <Download className="mr-2 h-4 w-4" />
                      Export to CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <Download className="mr-2 h-4 w-4" />
                      Export to Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="space-y-4 p-4">
          {/* Header Details Card */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
            <div className="p-6">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Header Details
              </h3>
              <div className="grid grid-cols-12 gap-4">
                {/* Row 1: Reference, Disposal Reason, Notes (right side) */}
                <div className="col-span-2">
                  <Label htmlFor="reference" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Reference *
                  </Label>
                  <Input
                    id="reference"
                    value={batchReference}
                    onChange={(e) => setBatchReference(e.target.value.slice(0, 10))}
                    placeholder="Enter reference..."
                    maxLength={10}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={1}
                  />
                </div>

                {/* Disposal Reason */}
                <div className="col-span-4">
                  <Label htmlFor="reason" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Disposal Reason
                  </Label>
                  <Select value={disposalReasonId} onValueChange={setDisposalReasonId} disabled={isReadOnly} data-readonly={isReadOnly ? '' : undefined}>
                    <SelectTrigger id="reason" className="h-9 placeholder:italic" style={{ fontStyle: 'normal' }} tabIndex={2}>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {disposalReasons.map((reason) => (
                        <SelectItem key={reason.id} value={reason.id}>
                          {reason.code} - {reason.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes - Right side spanning 2 rows */}
                <div className="col-span-6 row-span-2">
                  <Label htmlFor="notes" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value.slice(0, 400))}
                    placeholder="Enter any additional notes..."
                    rows={4}
                    maxLength={400}
                    readOnly={isReadOnly}
                    className="resize-none text-sm placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={6}
                  />
                  <p className="text-xs text-muted-foreground text-right mt-1">{notes.length}/400</p>
                </div>

                {/* Row 2: Date, Year, Period */}
                <div className="col-span-2">
                  <Label htmlFor="date" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Date *
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={dateOfRun}
                    onChange={(e) => setDateOfRun(e.target.value)}
                    readOnly={isReadOnly}
                    className="h-9 placeholder:italic"
                    style={{ fontStyle: 'normal' }}
                    tabIndex={3}
                  />
                </div>

                {/* Year */}
                <div className="col-span-2">
                  <Label htmlFor="year" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Year *
                  </Label>
                  <Select 
                    value={year.toString()} 
                    onValueChange={(value: string) => setYear(parseInt(value))} 
                    disabled={isReadOnly || isMultipleAssets}
                    data-readonly={isReadOnly ? '' : undefined}
                  >
                    <SelectTrigger id="year" className="h-9" tabIndex={4}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 5 }, (_, i) => currentSystemYear - i).map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Period */}
                <div className="col-span-2">
                  <Label htmlFor="period" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Period *
                  </Label>
                  <Input
                    id="period"
                    type="number"
                    min="1"
                    max="12"
                    value={period}
                    onChange={(e) => setPeriod(parseInt(e.target.value) || 1)}
                    readOnly={isReadOnly || isMultipleAssets}
                    className="h-9"
                    tabIndex={5}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Disposal Options and Values Card - Side by Side */}
          {items.length > 0 && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg">
              <div className="grid grid-cols-12 gap-0">
                {/* Left Side - Disposal Options */}
                <div className="col-span-4 bg-slate-50 dark:bg-slate-800/50 p-6 border-r border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Info className="h-5 w-5 text-primary" />
                    Options
                  </h3>

                  <div className="space-y-4">
                      {/* Dispose Of Mode */}
                      <div>
                        <Label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 block">Dispose of</Label>
                        <RadioGroup 
                          value={disposeOfMode} 
                          onValueChange={(value: DisposeOfMode) => setDisposeOfMode(value)} 
                          disabled={isReadOnly || isMultipleAssets}
                          data-readonly={isReadOnly ? '' : undefined}
                          className="space-y-1"
                        >
                          <div className={`flex items-center space-x-2 p-1.5 rounded-lg transition-colors ${shouldDisableValueMode ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                            <RadioGroupItem value="value" id="value" disabled={shouldDisableValueMode} />
                            <Label htmlFor="value" className={`font-normal flex items-center gap-2 text-sm ${shouldDisableValueMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                              <Coins className="h-3.5 w-3.5 text-muted-foreground" />
                              Value
                              {shouldDisableValueMode && (
                                <span className="text-xs text-amber-600 dark:text-amber-400 ml-1">(Depreciation posted)</span>
                              )}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                            <RadioGroupItem value="percentage" id="percentage" />
                            <Label htmlFor="percentage" className="font-normal cursor-pointer flex items-center gap-2 text-sm">
                              <Percent className="h-3.5 w-3.5 text-muted-foreground" />
                              Percentage
                            </Label>
                          </div>
                          {isSingleAsset && (
                            <div className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                              <RadioGroupItem value="quantity" id="quantity" />
                              <Label htmlFor="quantity" className="font-normal cursor-pointer flex items-center gap-2 text-sm">
                                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                                Quantity
                              </Label>
                            </div>
                          )}
                        </RadioGroup>
                      </div>

                      {/* Quantity Fields (only for single asset) */}
                      {isSingleAsset && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="currentQuantity" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                              Current Quantity
                            </Label>
                            <Input
                              id="currentQuantity"
                              type="number"
                              value={currentQuantity}
                              disabled
                              className="h-9"
                            />
                          </div>

                          {disposeOfMode === 'quantity' && (
                            <div>
                              <Label htmlFor="disposalQuantity" className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                Disposal Quantity *
                              </Label>
                              <Input
                                id="disposalQuantity"
                                type="number"
                                min="1"
                                max={currentQuantity}
                                value={disposalQuantity}
                                onChange={(e) => setDisposalQuantity(parseInt(e.target.value) || 0)}
                                disabled={isReadOnly}
                                className="h-9"
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Depreciation Handling */}
                      {showDepreciationHandling && (
                        <div className="space-y-2">
                          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                            <div className="flex gap-2">
                              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-amber-900 dark:text-amber-100">
                                Depreciation posted in current period. Select handling method.
                              </p>
                            </div>
                          </div>

                          <RadioGroup 
                            value={depreciationHandling || ''} 
                            onValueChange={(value: DepreciationHandling) => setDepreciationHandling(value)} 
                            disabled={isReadOnly}
                            data-readonly={isReadOnly ? '' : undefined}
                            className="space-y-1"
                          >
                            <div className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                              <RadioGroupItem value="reverse" id="reverse" />
                              <Label htmlFor="reverse" className="font-normal cursor-pointer text-sm">
                                Reverse
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                              <RadioGroupItem value="dispose" id="dispose" />
                              <Label htmlFor="dispose" className="font-normal cursor-pointer text-sm">
                                Dispose
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}

                      {/* Info Box for Multiple Assets */}
                      {isMultipleAssets && (
                        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                          <div className="flex gap-2">
                            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-900 dark:text-blue-100">
                              Multiple assets selected. Values are calculated and distributed proportionally.
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                {/* Right Side - Disposal Values */}
                <div className="col-span-8 p-6">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Coins className="h-5 w-5 text-primary" />
                    Disposal Values
                  </h3>

                  {/* Values Grid */}
                  <div className="grid grid-cols-2 gap-4">
                      {/* Actual Cost */}
                      <div className="space-y-2">
                        <Label htmlFor="actualCost" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          {disposeOfMode === 'percentage' ? 'Actual Cost %' : 'Actual Cost'}
                        </Label>
                        {disposeOfMode === 'percentage' ? (
                          <div className="relative">
                            <Input
                              id="actualCost"
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={disposalValues.actualCost}
                              onChange={(e) => setDisposalValues(prev => ({ ...prev, actualCost: parseFloat(e.target.value) || 0 }))}
                              className="h-10 pr-8 text-lg font-semibold"
                              readOnly={isReadOnly}
                              disabled={isMultipleAssets}
                            />
                            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          </div>
                        ) : (
                          <CurrencyInput
                            value={disposalValues.actualCost}
                            onChange={(value) => setDisposalValues(prev => ({ ...prev, actualCost: value }))}
                            className="h-10 text-lg font-semibold"
                            readOnly={isReadOnly}
                            disabled={isMultipleAssets || disposeOfMode === 'quantity'}
                          />
                        )}
                      </div>

                      {/* Historical Cost */}
                      <div className="space-y-2">
                        <Label htmlFor="historicalCost" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          {disposeOfMode === 'percentage' ? 'Historical Cost %' : 'Historical Cost'}
                        </Label>
                        {disposeOfMode === 'percentage' ? (
                          <div className="relative">
                            <Input
                              id="historicalCost"
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={disposalValues.historicalCost}
                              onChange={(e) => setDisposalValues(prev => ({ ...prev, historicalCost: parseFloat(e.target.value) || 0 }))}
                              className="h-10 pr-8 text-lg font-semibold"
                              readOnly={isReadOnly}
                              disabled={isMultipleAssets}
                            />
                            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          </div>
                        ) : (
                          <CurrencyInput
                            value={disposalValues.historicalCost}
                            onChange={(value) => setDisposalValues(prev => ({ ...prev, historicalCost: value }))}
                            className="h-10 text-lg font-semibold"
                            readOnly={isReadOnly}
                            disabled={isMultipleAssets || disposeOfMode === 'quantity'}
                          />
                        )}
                      </div>

                      {/* Actual Depreciation */}
                      <div className="space-y-2">
                        <Label htmlFor="actualDepreciation" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          {disposeOfMode === 'percentage' ? 'Actual Depreciation %' : 'Actual Depreciation'}
                        </Label>
                        {disposeOfMode === 'percentage' ? (
                          <div className="relative">
                            <Input
                              id="actualDepreciation"
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={disposalValues.actualDepreciation}
                              onChange={(e) => setDisposalValues(prev => ({ ...prev, actualDepreciation: parseFloat(e.target.value) || 0 }))}
                              className="h-10 pr-8 text-lg font-semibold"
                              readOnly={isReadOnly}
                              disabled={isMultipleAssets}
                            />
                            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          </div>
                        ) : (
                          <CurrencyInput
                            value={disposalValues.actualDepreciation}
                            onChange={(value) => setDisposalValues(prev => ({ ...prev, actualDepreciation: value }))}
                            className="h-10 text-lg font-semibold"
                            readOnly={isReadOnly}
                            disabled={isMultipleAssets || disposeOfMode === 'quantity'}
                          />
                        )}
                      </div>

                      {/* Historical Depreciation */}
                      <div className="space-y-2">
                        <Label htmlFor="historicalDepreciation" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          {disposeOfMode === 'percentage' ? 'Historical Depreciation %' : 'Historical Depreciation'}
                        </Label>
                        {disposeOfMode === 'percentage' ? (
                          <div className="relative">
                            <Input
                              id="historicalDepreciation"
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={disposalValues.historicalDepreciation}
                              onChange={(e) => setDisposalValues(prev => ({ ...prev, historicalDepreciation: parseFloat(e.target.value) || 0 }))}
                              className="h-10 pr-8 text-lg font-semibold"
                              readOnly={isReadOnly}
                              disabled={isMultipleAssets}
                            />
                            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          </div>
                        ) : (
                          <CurrencyInput
                            value={disposalValues.historicalDepreciation}
                            onChange={(value) => setDisposalValues(prev => ({ ...prev, historicalDepreciation: value }))}
                            className="h-10 text-lg font-semibold"
                            readOnly={isReadOnly}
                            disabled={isMultipleAssets || disposeOfMode === 'quantity'}
                          />
                        )}
                      </div>

                      {/* Proceeds Value - Full Width */}
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="proceedsValue" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          Proceeds Value
                        </Label>
                        <CurrencyInput
                          id="proceedsValue"
                          value={disposalValues.proceedsValue}
                          onChange={(value) => setDisposalValues(prev => ({ ...prev, proceedsValue: value }))}
                          className="h-10 text-lg font-semibold"
                          readOnly={isReadOnly}
                        />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Details Table */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden rounded-lg mb-[50px]">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Transaction Details
                <Badge variant="secondary" className="ml-2">{items.length} {items.length === 1 ? 'Asset' : 'Assets'}</Badge>
              </h3>
              {!isReadOnly && (
                <div className="flex items-center gap-2">
                  {selectedItemIds.size > 0 && (
                    <Button type="button" variant="destructive" size="sm" onClick={handleRemoveSelectedItems} className="h-8">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Remove Lines ({selectedItemIds.size})
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={handleAddMoreAssets} className="h-8">
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    Add Assets
                    </Button>
                  </div>
                )}
              </div>

            {items.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                    <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                      {!isReadOnly && (
                        <TableHead className="h-12 font-semibold w-[40px]">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={handleSelectAll}
                            aria-label="Select all"
                          />
                        </TableHead>
                      )}
                      <TableHead className="h-12 font-semibold">Asset Code</TableHead>
                      <TableHead className="h-12 font-semibold">Description</TableHead>
                      <TableHead className="h-12 font-semibold text-right">Current Cost</TableHead>
                      <TableHead className="h-12 font-semibold text-right">Current Depr.</TableHead>
                      <TableHead className="h-12 font-semibold text-right">Current NBV</TableHead>
                      <TableHead className="h-12 font-semibold text-right bg-blue-50/50 dark:bg-blue-950/20 border-l-2 border-slate-300 dark:border-slate-600">Disposal Cost</TableHead>
                      <TableHead className="h-12 font-semibold text-right bg-blue-50/50 dark:bg-blue-950/20">Disposal Depr.</TableHead>
                      <TableHead className="h-12 font-semibold text-right bg-blue-50/50 dark:bg-blue-950/20">Proceeds</TableHead>
                      <TableHead className="h-12 font-semibold text-right bg-blue-50/50 dark:bg-blue-950/20">NBV after Disposal</TableHead>
                      <TableHead className="h-12 font-semibold text-right bg-blue-50/50 dark:bg-blue-950/20">Gain/Loss</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calculatedItems.map((item, index) => (
                      <TableRow 
                        key={item.id} 
                        className={`
                          ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                          hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                        `}
                      >
                        {!isReadOnly && (
                          <TableCell className="w-[40px]">
                            <Checkbox
                              checked={selectedItemIds.has(item.id)}
                              onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                              aria-label={`Select ${item.assetCode}`}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium text-sm">{item.assetCode}</TableCell>
                        <TableCell className="text-sm">{item.assetDescription}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(item.currentCost)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(item.currentDepreciation)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(item.currentNBV)}</TableCell>
                        <TableCell className="text-right bg-blue-50/30 dark:bg-blue-950/10 text-sm font-medium border-l-2 border-slate-300 dark:border-slate-600">{formatCurrency(item.disposalCost)}</TableCell>
                        <TableCell className="text-right bg-blue-50/30 dark:bg-blue-950/10 text-sm font-medium">{formatCurrency(item.disposalDepreciation)}</TableCell>
                        <TableCell className="text-right bg-blue-50/30 dark:bg-blue-950/10 text-sm font-medium">{formatCurrency(item.proceedsValue)}</TableCell>
                        <TableCell className="text-right font-semibold bg-blue-50/30 dark:bg-blue-950/10 text-sm">{formatCurrency(item.disposalNBV)}</TableCell>
                        <TableCell className={`text-right font-semibold bg-blue-50/30 dark:bg-blue-950/10 text-sm ${item.gainLoss >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(item.gainLoss)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-muted p-3 mb-4">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-1">No Assets Added</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add assets to this disposal batch to get started
                </p>
                {!isReadOnly && (
                  <Button variant="outline" size="sm" onClick={handleAddMoreAssets}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Assets
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Footer */}
      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-t border-slate-200 dark:border-slate-800 shadow-lg z-10">
          <div className="px-6 py-3">
            <div className="flex justify-end items-center gap-4 max-w-7xl ml-auto">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Disposal Cost</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalDisposalCost)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Disposal Depr.</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalDisposalDepreciation)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Proceeds</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalProceedsValue)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">NBV after Disposal</span>
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">{formatCurrency(totalDisposalNBV)}</span>
              </div>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Gain/Loss</span>
                <span className={`text-base font-bold ${totalGainLoss >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      <ConfirmationModal
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        onConfirm={confirmCancel}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        type="danger"
      />

      <ConfirmationModal
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        onConfirm={confirmProcess}
        title="Process Disposal?"
        description={`This will process the disposal "${batchReference}" and post transactions to the system. This action cannot be undone.`}
        confirmText="Process"
        type="warning"
      />

      <ConfirmationModal
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        onConfirm={confirmRemoveSelectedItems}
        title="Remove Selected Assets?"
        description={`Are you sure you want to remove ${selectedItemIds.size} selected ${selectedItemIds.size === 1 ? 'asset' : 'assets'} from this disposal batch?`}
        confirmText="Remove"
        type="danger"
      />

      {/* Asset Selection Dialog */}
      {showAssetSelection && (
        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          title="Select Assets for Disposal"
          description="Choose the assets you want to dispose of. You can select multiple assets to add to this disposal batch."
          confirmButtonLabel="Add Selected Assets"
        />
      )}
    </div>
  );
}
